/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { SubmitHandler } from 'react-hook-form';
import {
  useForm,
  useFetch,
  useApiClient,
  getFormProps,
  getFieldProps,
  getSubmitButtonProps,
} from '@marageti/z4-lib';
import { LoginRequest } from '@marageti/z4-sdk/lib/auth/Token';
import { Box, Stack, TextField } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { LoadingButton } from '@mui/lab';
import { PasswordConfig } from '@marageti/z4-sdk/lib/auth';

const useValidationSchema = ({ disablePasswordValidation } : {
  disablePasswordValidation?: boolean;
}) => {
  const apiClient = useApiClient();
  const { data: passwordConfig } = useFetch<PasswordConfig>(apiClient.authClient.getPasswordConfig, [], true);

  const schema = z.object({
    username: z.string().min(1, 'Required'),
    password: z.string().min(1, 'Please enter a password')
      .refine(async (value) => {
        if (disablePasswordValidation) return true;
        if (!passwordConfig) return false;
        const regex = new RegExp(passwordConfig.filter);
        return regex.test(value);
      }, {
        message: passwordConfig?.description,
      }),
  });

  return schema;
};

export interface PasswordLoginViewProps {
  defaultValues?: LoginRequest;
  disablePasswordValidation?: boolean;
  emailReadOnly?: boolean;
  error?: React.ReactNode
  onSubmit: SubmitHandler<LoginRequest>;
}

const PasswordLogin = ({
  defaultValues = undefined,
  disablePasswordValidation = false,
  emailReadOnly = false,
  error = undefined,
  onSubmit,
}: PasswordLoginViewProps) => {
  const schema = useValidationSchema({ disablePasswordValidation });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<LoginRequest>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  return (
    <Box minWidth={340}>
      <form {...getFormProps(handleSubmit(onSubmit))}>
        <Stack spacing={3}>
          <Stack>

            <TextField
              {...register('username')}
              {...getFieldProps('username', errors)}
              data-e2e="ztest-username"
              id="username"
              label="Email"
              InputProps={{
                readOnly: emailReadOnly,
              }}
              autoComplete="email"
            />
            <TextField
              {...register('password')}
              {...getFieldProps('password', errors)}
              data-e2e="ztest-password"
              id="password"
              label="Password"
              type="password"
              autoComplete="current-password"
            />
          </Stack>

          {error && (error)}

          <LoadingButton
            data-e2e="ztest-login-submit"
            type="submit"
            variant="contained"
            fullWidth
            {...getSubmitButtonProps(isSubmitting, isValid)}
          >
            Sign In
          </LoadingButton>
        </Stack>
      </form>
    </Box>
  );
};

export default PasswordLogin;
